import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";

export const PLATFORM_DOCUMENTS_BUCKET = "platform-documents";

export type PlatformDocumentType = "terms.pdf" | "privacy-policy.pdf" | "website-terms.pdf" | "platform-fees.pdf";

export const getFallbackUrl = (docType: PlatformDocumentType) => {
    return `/documents/${docType}`;
};

/**
 * Public URL of the uploaded document. The bucket is public and admin uploads
 * overwrite the same object, so this serves the current version even before the
 * listing has resolved or when it fails. The static `/documents/` copies are not
 * kept in sync with uploads, so they are only used once the listing confirms
 * nothing has been uploaded.
 */
export const getStorageUrl = (docType: PlatformDocumentType) => {
    return supabase.storage.from(PLATFORM_DOCUMENTS_BUCKET).getPublicUrl(docType).data.publicUrl;
};

export interface PlatformDocumentState {
    url: string;
    isLoading: boolean;
    error: Error | null;
    lastModified: Date | null;
}

interface StorageFile {
    name: string;
    id: string;
    updated_at?: string | null;
    created_at?: string | null;
    last_accessed_at?: string | null;
    metadata?: Record<string, unknown> | null;
}

// Module-level cache variables to deduplicate and cache requests
let cachedFiles: StorageFile[] | null = null;
let activeListPromise: Promise<StorageFile[]> | null = null;
let lastFetchTime = 0;
const CACHE_TTL = 60 * 1000; // Cache listings for 1 minute
let cacheGeneration = 0;

// Invalidate listing cache on auth state changes (e.g. login/logout) to prevent session data leak
const { data: { subscription: platformDocsAuthSub } } = supabase.auth.onAuthStateChange((event) => {
    if (event === "SIGNED_IN" || event === "SIGNED_OUT" || event === "USER_UPDATED" || event === "TOKEN_REFRESHED") {
        cacheGeneration += 1;
        cachedFiles = null;
        lastFetchTime = 0;
        activeListPromise = null;
    }
});

if (import.meta.hot) import.meta.hot.dispose(() => platformDocsAuthSub.unsubscribe());

const fetchFilesList = async (): Promise<StorageFile[]> => {
    const now = Date.now();
    if (cachedFiles && (now - lastFetchTime < CACHE_TTL)) {
        return cachedFiles;
    }

    if (activeListPromise) {
        return activeListPromise;
    }

    const generationAtStart = cacheGeneration;

    activeListPromise = (async () => {
        try {
            const { data, error } = await supabase.storage
                .from(PLATFORM_DOCUMENTS_BUCKET)
                .list("", { limit: 100 });

            if (error) {
                throw error;
            }

            const files = (data as StorageFile[]) || [];
            if (cacheGeneration === generationAtStart) {
                cachedFiles = files;
                lastFetchTime = Date.now();
            }
            return files;
        } finally {
            activeListPromise = null;
        }
    })();

    return activeListPromise;
};

// Cache-bust using the file's last modified timestamp
const getVersionedStorageUrl = (docType: PlatformDocumentType, docFile: StorageFile) => {
    const cacheBuster = docFile.updated_at
        ? `?t=${new Date(docFile.updated_at).getTime()}`
        : `?t=${Date.now()}`;
    return getStorageUrl(docType) + cacheBuster;
};

/**
 * Hook to fetch a platform document PDF URL from Supabase storage.
 * Falls back to the static file only if no uploaded version exists.
 */
export const usePlatformDocument = (docType: PlatformDocumentType) => {
    const fallbackUrl = getFallbackUrl(docType);

    // Start on the uploaded document so a link clicked before the listing
    // resolves still opens the current version, not the static copy.
    const [state, setState] = useState<PlatformDocumentState>(() => ({
        url: getStorageUrl(docType),
        isLoading: true,
        error: null,
        lastModified: null,
    }));

    useEffect(() => {
        let isMounted = true;

        const fetchDocumentUrl = async () => {
            try {
                const files = await fetchFilesList();

                if (!isMounted) return;

                const docFile = files?.find((f) =>
                    f.name === docType
                );

                if (!docFile) {
                    // No uploaded file, use fallback
                    setState({
                        url: fallbackUrl,
                        isLoading: false,
                        error: null,
                        lastModified: null,
                    });
                    return;
                }

                setState({
                    url: getVersionedStorageUrl(docType, docFile),
                    isLoading: false,
                    error: null,
                    lastModified: docFile.updated_at
                        ? new Date(docFile.updated_at)
                        : null,
                });
            } catch (err) {
                if (!isMounted) return;
                console.error(`Error fetching ${docType} URL:`, err);
                setState({
                    url: getStorageUrl(docType),
                    isLoading: false,
                    error: err instanceof Error
                        ? err
                        : new Error("Unknown error"),
                    lastModified: null,
                });
            }
        };

        fetchDocumentUrl();

        return () => {
            isMounted = false;
        };
    }, [docType, fallbackUrl]);

    return state;
};

/**
 * Utility function to get the document URL asynchronously (for non-component use).
 * Resolves to the public URL (with cache buster if available), the fallback URL
 * when nothing has been uploaded, or the un-busted public URL on error.
 */
export const getPlatformDocumentUrl = async (docType: PlatformDocumentType): Promise<string> => {
    try {
        const files = await fetchFilesList();
        const docFile = files?.find((f) => f.name === docType);

        if (!docFile) {
            return getFallbackUrl(docType);
        }

        return getVersionedStorageUrl(docType, docFile);
    } catch {
        return getStorageUrl(docType);
    }
};

/**
 * Upload a new platform document PDF (admin only)
 */
export const uploadPlatformDocument = async (
    file: File,
    docType: PlatformDocumentType,
): Promise<{ success: boolean; error?: string }> => {
    try {
        const { error } = await supabase.storage
            .from(PLATFORM_DOCUMENTS_BUCKET)
            .upload(docType, file, {
                cacheControl: "3600",
                upsert: true, // Overwrite existing file
                contentType: "application/pdf",
            });

        if (error) {
            return { success: false, error: error.message };
        }

        // Invalidate listing cache on successful upload
        cacheGeneration += 1;
        cachedFiles = null;
        lastFetchTime = 0;
        activeListPromise = null;
        return { success: true };
    } catch (err) {
        return {
            success: false,
            error: err instanceof Error
                ? err.message
                : "Unknown error uploading file",
        };
    }
};
